import { AudioVisualizer } from "@/components/ai-elements/speech-input";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CheckIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type VoiceRecordingBarProps = {
  /** Live recording stream (media-recorder mode); null lets the visualizer self-acquire. */
  stream: MediaStream | null;
  /** Discard the recording and return to the text input. */
  onCancel: () => void;
  /** Stop recording and keep the transcription in the editable input (no send). */
  onConfirm: () => void;
  /**
   * "bar" — standalone surface with its own border and padding.
   * "inline" — controls embedded in the composer toolbar, replacing the
   * mic / model / send buttons.
   */
  variant?: "bar" | "inline";
  /**
   * Let the waveform fill the available width instead of the fixed inline
   * width. Used on mobile, where the toolbar row is narrower.
   */
  fluid?: boolean;
  /**
   * False once live transcription is lost. Recording continues, so the bar
   * says the text is coming rather than letting a frozen sentence imply a crash.
   */
  isLiveTranscript?: boolean;
};

const formatElapsed = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Recording surface shown while dictating: a discard / waveform / confirm
 * control set. Rendered inline in the surrounding toolbar so the field above
 * stays visible and the transcript can be read as it streams in.
 *
 * Shared by every dictation entry point — the chat composer and the company
 * note — so stopping a dictation looks and behaves the same everywhere.
 */
export default function VoiceRecordingBar({
  stream,
  onCancel,
  onConfirm,
  variant = "bar",
  fluid = false,
  isLiveTranscript = true,
}: VoiceRecordingBarProps) {
  const { t } = useTranslation();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Resolve the primary (majorelle) color from the theme for canvas rendering.
  const barColor = useMemo(() => {
    if (typeof document === "undefined") return "#6647FC";
    const style = getComputedStyle(document.documentElement);
    const majorelle = style.getPropertyValue("--majorelle").trim();
    return majorelle ? `hsl(${majorelle})` : "#6647FC";
  }, []);

  const isInline = variant === "inline";

  return (
    <div
      className={cn(
        "flex gap-2 items-center",
        !isInline && "p-2 rounded-2xl border bg-background",
        fluid && "flex-1 min-w-0",
      )}
    >
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="rounded-full shadow-none text-primary hover:bg-primary/10 hover:text-primary"
        onClick={onCancel}
        aria-label={t("chat.input.speech.cancel")}
      >
        <XIcon className="size-4" />
      </Button>

      <div
        className={cn(
          "flex gap-2 items-center px-3 h-9 rounded-full min-w-0 bg-primary/10",
          isInline && !fluid ? "w-56" : "flex-1",
        )}
      >
        <span className="flex relative size-2 shrink-0">
          <span className="inline-flex absolute size-full rounded-full opacity-75 animate-ping bg-primary" />
          <span className="inline-flex relative size-2 rounded-full bg-primary" />
        </span>
        <span className="text-sm font-medium tabular-nums shrink-0 text-primary">
          {formatElapsed(elapsedSeconds)}
        </span>
        {!isLiveTranscript && (
          <span className="text-xs truncate text-primary/80">
            {t("chat.input.speech.offline")}
          </span>
        )}
        <div className="flex-1 min-w-0">
          {/* Fluid: the bar fits as many bars as the pill allows, so they stay
              the same width here as next to the microphone button. */}
          <AudioVisualizer stream={stream} barColor={barColor} fluid />
        </div>
      </div>

      <Button
        type="button"
        size="icon"
        className="rounded-full"
        onClick={onConfirm}
        aria-label={t("chat.input.speech.stopAndEdit")}
      >
        <CheckIcon className="size-4" />
      </Button>
    </div>
  );
}
